import React, { useState, useEffect } from 'react';
import CompanyProfileForm from './Company/Profile/CompanyProfileForm';
import EmployeeProfileForm from './Employee/Profile/EmployeeProfileForm';
import { checkIcon } from '../../../utils/icons';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../../context/AuthContext';
import './profileView.css'

const ProfileView = ({ userType, restaurantId, isNewRestaurant, onFormSubmit }) => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);
  const [flashMessage, setFlashMessage] = useState(null);
  const [showFirstJobModal, setShowFirstJobModal] = useState(false);

  useEffect(() => {
    if (isNewRestaurant) {
      // For new restaurant creation, don't fetch existing profile
      setProfile(null);
      setShowForm(true);
    } else {
      fetchProfile();
    }
  }, [isNewRestaurant, restaurantId]);

  const fetchProfile = async () => {
    const endpoint = userType === 'empresas' ? 'company' : 'employee';
    try {
      
      // Build URL with restaurantId if provided (for editing specific restaurant)
      const url = restaurantId 
        ? `${process.env.REACT_APP_BACKEND_STAGING_URL}/api/${endpoint}?restaurantId=${restaurantId}`
        : `${process.env.REACT_APP_BACKEND_STAGING_URL}/api/${endpoint}`;
      
      
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
      });


      if (!response.ok) {
        setProfile(null);
        return;
      }

      const data = await response.json();

      if (data.success && data.data) {
        setProfile(data.data);
      } else {
        setProfile(null);
      }
    } catch (error) {
      console.error(`Error fetching profile:`, error);
      setProfile(null);
    }
  };

  const handleAddClick = () => {
    setShowForm(true);
    setIsUpdate(false);
  };

  const handleFormSubmit = (isUpdate) => {
    setIsUpdate(isUpdate);
    setShowForm(false);
    fetchProfile();
    setFlashMessage(isUpdate ? 'Tu perfil ha sido actualizado' : 'Tu perfil está listo!');
    window.scrollTo({ top: 0, behavior: 'smooth' });

    // Show "Postula a tu primer trabajo" modal when professional creates profile for the first time
    if (!isUpdate && userType === 'employee') {
      setShowFirstJobModal(true);
    }

    // If we have an onFormSubmit callback (from RestaurantManager), call it
    if (onFormSubmit) {
      onFormSubmit();
    }
  };

  const handleCloseFirstJobModal = () => {
    setShowFirstJobModal(false);
  };

  const handleGoToJobs = () => {
    setShowFirstJobModal(false);
    navigate('/trabajos');
  };

  const handleViewProfile = () => {
    try {
      if (userType === 'empresas') {
        const path = '/empresas/' + profile?.id;
        navigate(path);
      } else {
        // For employees, use the employee ID from the profile
        const employeeId = profile?.id || profile?.profile?.id;
        if (employeeId) {
          const path = '/empleados/' + employeeId;
          navigate(path);
        } else {
          console.error('No employee ID found in profile:', profile);
          // Fallback: try to get employee ID from user context or show error
          alert('No se pudo encontrar el perfil del empleado');
        }
      }
    } catch (error) {
      console.error('Error navigating to profile:', error);
      alert('Error al navegar al perfil');
    }
  };

  return (
    <div className="profile-container">
      {showFirstJobModal && (
        <div className="profile-view__modal-overlay" onClick={handleCloseFirstJobModal}>
          <div className="profile-view__modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="profile-view__modal-title">¡Tu perfil está listo!</h3>
            <p className="profile-view__modal-text">
              Ahora puedes postular a tu primer trabajo. Explora las ofertas disponibles y aplica a la que más te interese.
            </p>
            <div className="profile-view__modal-actions">
              <button type="button" className="profile-view__modal-button profile-view__modal-button--primary" onClick={handleGoToJobs}>
                Ver trabajos
              </button>
              <button type="button" className="profile-view__modal-button profile-view__modal-button--secondary" onClick={handleCloseFirstJobModal}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
      {flashMessage && (
        <div className={`flash-message ${flashMessage.type}`}>
          <p id="flash-message-text">{flashMessage}</p>
          {checkIcon}
        </div>
      )}

      {profile ? (
        <div className="profile-view__profile-container">
          <div className="profile-view__title-container">
            <h1>Edita tu perfil</h1>
            <button className="profile-view__button" onClick={handleViewProfile}>Ver Mi Perfil</button>
          </div>
          <div className="profile-form__container">
            {userType === "empresas" ? (
              <CompanyProfileForm
                onFormSubmit={() => handleFormSubmit(true)}
                initialValues={profile}
                isNewRestaurant={false}
                restaurantId={restaurantId}
              />
            ) : (
              <EmployeeProfileForm
                onFormSubmit={() => handleFormSubmit(true)}
                initialValues={profile}
                isEditing={true}
                setFlashMessage={setFlashMessage}
              />
            )}
          </div>
        </div>
      ) : (
        <div className="profile-view__container">
          <h1>{`Crea tu ${
            userType === "empresas" ? "perfil de empresa" : "perfil de empleado"
          }`}</h1>
          {showForm ? (
            userType === "empresas" ? (
              <CompanyProfileForm
                onFormSubmit={() => handleFormSubmit(true)}
                setFlashMessage={setFlashMessage}
                isNewRestaurant={isNewRestaurant}
              />
            ) : (
              <EmployeeProfileForm
                onFormSubmit={() => handleFormSubmit(false)}
                setFlashMessage={setFlashMessage}
              />
            )
          ) : (
            <div className="profile-view__empty">
              {userType === "empresas" ? (
                <button className="profile-view__button" onClick={handleAddClick}>
                  + Crear
                </button>
              ) : (
                <>
                  <div className="profile-view__empty-icon">👤</div>
                  <h3>No tienes perfil de empleado creado</h3>
                  <p>Crea tu perfil de empleado para comenzar a buscar oportunidades laborales</p>
                  <button className="profile-view__button" onClick={handleAddClick}>
                    Crear Mi Perfil de Empleado
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProfileView;
